import React, { useState, useEffect } from 'react';
import { Grid, Paper, Box, Typography, Link, CircularProgress } from '@mui/material';
import Card from '../charts/card';
import ChartPadding from '../charts/chart_padding';
import { dataDashboards } from '../data/data_dashboards';
import DashboardHeader from './dashboard_header';

const Dashboard = ({ dashboardId }) => {
    const [dashboardState, setDashboardState] = useState({
        dashboard: null,
        cards: [],
        layout: [],
        layoutRows: [],
        isLoaded: false,
        error: null
    });

    useEffect(() => {
        dataDashboards.loadDashboard(dashboardId)
            .then(result => {
                if (result) {
                    const { dashboard, charts, layout, layoutRows } = result;
                    const renderRows = generateRows(layoutRows, charts);
                    setDashboardState(prev => ({
                        ...prev,
                        dashboard: dashboard[0],
                        cards: charts,
                        layout,
                        layoutRows,
                        isLoaded: true,
                        renderRows
                    }));
                }
            })
            .catch(error => {
                setDashboardState(prev => ({
                    ...prev,
                    isLoaded: true,
                    error
                }));
            });
    }, [dashboardId]);

    const generateRows = (layoutRows, cards) => {
        if (!layoutRows || !cards) return [];

        const sortedLayoutRows = [...layoutRows].sort((a, b) => {
            if (a.row_ordinal !== b.row_ordinal) {
                return a.row_ordinal - b.row_ordinal;
            }
            return a.column_ordinal - b.column_ordinal;
        });

        const rows = [];
        let currentRow = null;
        let rowCards = [];

        sortedLayoutRows.forEach(layout => {
            if (currentRow === null || currentRow !== layout.row_ordinal) {
                if (rowCards.length > 0) {
                    rows.push({
                        row: currentRow,
                        cards: rowCards,
                        totalColumns: rowCards.length
                    });
                }
                currentRow = layout.row_ordinal;
                rowCards = [];
            }

            const card = cards.find(card => card?.chart_id === layout.card_id);
            if (card) {
                rowCards.push({
                    ...card,
                    height: layout.height,
                    width: layout.width
                });
            }
        });

        if (rowCards.length > 0) {
            rows.push({
                row: currentRow,
                cards: rowCards,
                totalColumns: rowCards.length
            });
        }

        return rows;
    };

    const renderChartCard = (card) => {
        // Find the layout row for this card to get height and width
        const layoutRow = dashboardState.layoutRows?.find(
            layout => layout.card_id === card.chart_id
        );

        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: layoutRow?.height || card.height || 300
            }}>
                <Card 
                    key={card.chart_id} 
                    chart_id={card.chart_id} 
                    dashboard_id={dashboardState.dashboard?.id}
                    height={layoutRow?.height || card.height} 
                    width={layoutRow?.width || card.width} 
                />
            </Box>
        );
    };

    const renderRow = (row) => {
        if (!row?.cards?.length) return null;

        const getColumnWidth = (totalColumns) => {
            if (totalColumns === 1) return 12;
            if (totalColumns === 2) return 6;
            return 3;
        };

        return (
            <Grid 
                container 
                spacing={1} 
                key={row.row} 
                sx={{ px: 3, py: 1.5, justifyContent: 'center' }}
            >
                {row.cards.map((card) => (
                    <Grid
                        item
                        key={card.chart_id}
                        xs={12}
                        sm={getColumnWidth(row.totalColumns)}
                        sx={{ display: 'flex', justifyContent: 'center' }}
                    >
                        {renderChartCard(card)}
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderBasicLayout = () => {
        if (!dashboardState.cards?.length) return null;

        return dashboardState.cards.map(card => (
            <Box key={card.chart_id} sx={{ py: 3, px: 3 }}>
                <Grid container justifyContent="center">
                    <Grid item xs={12}>
                        {renderChartCard(card)}
                    </Grid>
                </Grid>
            </Box>
        ));
    };

    if (!dashboardState.isLoaded) return null;
    if (dashboardState.error) return <div>Error loading dashboard</div>;

    return (
        <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
            <DashboardHeader 
                title={dashboardState.dashboard?.title} 
                description={dashboardState.dashboard?.description} 
            />
            {dashboardState.layout?.length > 0 && dashboardState.renderRows?.length > 0
                ? dashboardState.renderRows.map(renderRow)
                : renderBasicLayout()}
            <ChartPadding />
        </Box>
    );
};

export default Dashboard;